import Component from '@glimmer/component';

// ---------------------------------------------------------------------------
// Chip – reimagined, zero-Frontile chip / badge / tag component
// ---------------------------------------------------------------------------

/**
 * Visual intent/severity options
 */
export type ChipIntent = 'primary' | 'danger' | 'warning' | 'success' | 'default';

/**
 * Size options
 */
export type ChipSize = 'sm' | 'md' | 'lg';

/**
 * Visual appearance / variant
 */
export type ChipAppearance = 'default' | 'outlined' | 'faded';

export interface ChipSignature {
  Args: {
    /** Visual intent */
    intent?: ChipIntent;
    /** Size */
    size?: ChipSize;
    /** Visual appearance (default = filled, outlined = bordered, faded = subtle bg) */
    appearance?: ChipAppearance;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLSpanElement;
}

// ---------------------------------------------------------------------------
// Style maps (Tailwind classes only)
// ---------------------------------------------------------------------------

/** Default (filled) appearance – solid background */
const INTENT_CLASSES: Record<ChipIntent, string> = {
  primary: 'bg-[#006c9a]/10 text-[#006c9a] dark:bg-[#006c9a]/20 dark:text-[#4db8e0]',
  danger: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  default: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

/** Outlined appearance – border only, transparent bg */
const OUTLINED_CLASSES: Record<ChipIntent, string> = {
  primary: 'border border-[#006c9a]/30 text-[#006c9a] dark:border-[#006c9a]/40 dark:text-[#4db8e0]',
  danger: 'border border-red-300 text-red-700 dark:border-red-700 dark:text-red-300',
  warning: 'border border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300',
  success:
    'border border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300',
  default: 'border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300',
};

/** Faded appearance – very subtle background, lighter than default */
const FADED_CLASSES: Record<ChipIntent, string> = {
  primary: 'bg-[#006c9a]/5 text-[#006c9a] dark:bg-[#006c9a]/10 dark:text-[#4db8e0]',
  danger: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
  default: 'bg-gray-50 text-gray-700 dark:bg-gray-800/50 dark:text-gray-300',
};

const APPEARANCE_MAP: Record<ChipAppearance, Record<ChipIntent, string>> = {
  default: INTENT_CLASSES,
  outlined: OUTLINED_CLASSES,
  faded: FADED_CLASSES,
};

const SIZE_CLASSES: Record<ChipSize, string> = {
  sm: 'text-xs px-2 py-0.5 rounded-md',
  md: 'text-sm px-2.5 py-1 rounded-lg',
  lg: 'text-sm px-3 py-1.5 rounded-lg',
};

/**
 * A lightweight status badge / tag used in cards, tables, and list items.
 *
 * ```gts
 * <Chip @intent="success" @size="sm">Active</Chip>
 * <Chip @intent="primary" @appearance="faded">Enrolled</Chip>
 * <Chip @intent="danger" @appearance="outlined">Rejected</Chip>
 * ```
 *
 * @public
 */
export default class Chip extends Component<ChipSignature> {
  get intent() {
    return this.args.intent ?? 'default';
  }

  get size() {
    return this.args.size ?? 'md';
  }

  get appearance() {
    return this.args.appearance ?? 'default';
  }

  get intentClass() {
    return APPEARANCE_MAP[this.appearance][this.intent];
  }

  get sizeClass() {
    return SIZE_CLASSES[this.size];
  }

  <template>
    <span
      data-test-chip
      data-test-chip-intent={{this.intent}}
      data-test-chip-size={{this.size}}
      class="inline-flex items-center gap-1 font-medium {{this.intentClass}} {{this.sizeClass}}"
      ...attributes
    >
      {{yield}}
    </span>
  </template>
}
